import { TableText } from "@/components/table-tools";
import { useState } from "react";
import {
  createConnectQueryKey,
  useMutation,
  useQuery,
} from "@connectrpc/connect-query";
import { useQueryClient } from "@tanstack/react-query";
import { KeyRoundIcon, TriangleAlertIcon } from "lucide-react";

import {
  adminCreateInvites,
  adminListInvites,
} from "@/gen/nodebase/v1/admin-AdminService_connectquery";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CopyButton } from "@/components/copy";
import { EmptyNote, ErrorNote, Loading, PageHeader } from "@/components/page";
import { FleetScope } from "@/components/scope";
import { fmtDate } from "@/lib/format";
import { cn } from "@/lib/utils";

const REGISTRATION_URL = "https://dash.nodebase.com/register";

function registrationURL(code: string): string {
  const url = new URL(REGISTRATION_URL);
  url.searchParams.set("invite", code);
  return url.toString();
}

export function AdminInvites() {
  const queryClient = useQueryClient();
  // The minted codes live in component state and nowhere else, on purpose. The
  // server stores only hashes, so this array is the single copy in existence —
  // caching it would be inventing a second one, and the whole point of hashing
  // is that there is not one.
  const [fresh, setFresh] = useState<string[] | null>(null);

  const [count, setCount] = useState(5);
  const [note, setNote] = useState("");
  const [maxUses, setMaxUses] = useState(1);
  const [expiresDays, setExpiresDays] = useState(30);

  const invites = useQuery(adminListInvites, {}, { retry: false });
  const create = useMutation(adminCreateInvites, {
    onSuccess: (res) => {
      setFresh(res.codes);
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: adminListInvites,
          cardinality: undefined,
        }),
      });
    },
  });

  const rows = invites.data?.invites ?? [];

  return (
    <>
      <PageHeader title="Invites" scope={<FleetScope noun="Invite codes" />} />
      <ErrorNote error={invites.error ?? create.error} />

      {fresh && <FreshCodes codes={fresh} onDone={() => setFresh(null)} />}

      <form
        className="mb-8 grid gap-4 rounded-lg border p-4 sm:grid-cols-5"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate({ count, note, maxUses, expiresDays });
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor="count">How many</Label>
          <Input
            id="count"
            name="count"
            type="number"
            min={1}
            value={count}
            onChange={(e) => setCount(Number(e.target.value))}
          />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="note">Note</Label>
          <Input
            id="note"
            name="note"
            placeholder="What are they for?"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="max-uses">Uses each</Label>
          <Input
            id="max-uses"
            name="maxUses"
            type="number"
            min={1}
            value={maxUses}
            onChange={(e) => setMaxUses(Number(e.target.value))}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="expires">Valid for (days)</Label>
          <Input
            id="expires"
            name="expiresDays"
            type="number"
            min={0}
            value={expiresDays}
            onChange={(e) => setExpiresDays(Number(e.target.value))}
          />
          <p className="text-xs text-muted-foreground">0 = never expires</p>
        </div>
        <div className="sm:col-span-5">
          <Button type="submit" disabled={create.isPending}>
            <KeyRoundIcon /> Issue invites
          </Button>
        </div>
      </form>

      {invites.isPending ? (
        <Loading />
      ) : invites.isError ? null : rows.length === 0 ? (
        <EmptyNote>No invites have been issued yet.</EmptyNote>
      ) : (
        <Table aria-label="Invites">
          <TableHeader>
            <TableRow>
              <TableHead>Note</TableHead>
              <TableHead>Used</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Redeemed by</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((v, i) => {
              const maximum = v.maxUses || 1;
              const spent = v.usedCount >= maximum;
              const expired =
                v.expiresUnix !== 0n &&
                Number(v.expiresUnix) * 1000 < Date.now();
              return (
                <TableRow
                  // AdminInvite carries no id and no code (codes are hashed), so
                  // there is no stable key to use; the list is server-ordered
                  // and read-only, which is the case an index key is safe for.
                  key={i}
                  data-testid="invite-row"
                  className={cn((spent || expired) && "opacity-50")}
                >
                  <TableCell className="font-medium"><TableText value={v.note || "—"} /></TableCell>
                  <TableCell>
                    {v.usedCount}/{maximum}
                  </TableCell>
                  <TableCell>
                    {v.expiresUnix === 0n
                      ? "never"
                      : fmtDate(v.expiresUnix) + (expired ? " (past)" : "")}
                  </TableCell>
                  <TableCell>{fmtDate(v.createdUnix)}</TableCell>
                  <TableCell><TableText value={v.redeemedBy.join(", ") || "—"} /></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </>
  );
}

// FreshCodes is the one place these strings will ever be readable. It stays put
// until explicitly dismissed rather than fading out on a timer, and the copy
// button is the primary action, because the failure mode — navigating away with
// codes nobody wrote down — is silent and unrecoverable.
function FreshCodes({
  codes,
  onDone,
}: {
  codes: string[];
  onDone: () => void;
}) {
  return (
    <div
      data-testid="fresh-invites"
      className="mb-6 rounded-lg border border-amber-500/50 bg-amber-500/10 p-4"
    >
      <div className="mb-3 flex items-start gap-2">
        <TriangleAlertIcon className="mt-0.5 size-4 shrink-0 text-amber-700 dark:text-amber-400" />
        <div className="flex-1">
          <p className="font-medium">
            Copy these now — they cannot be shown again.
          </p>
          <p className="text-sm text-muted-foreground">
            The control plane stores only a hash of each code, so this is the
            only time the plaintext exists. The list below will show that these
            invites exist and how they get used, but never what they say.
          </p>
        </div>
        {codes.length > 1 && (
          <CopyButton
            value={codes.map(registrationURL).join("\n")}
            label="Copy all registration links"
          />
        )}
      </div>

      {/* The full registration link is the handoff artifact. Keep an explicit
          code copy beside it for voice/manual channels, but do not make the
          recipient assemble a URL themselves in the normal path. */}
      <ul data-testid="fresh-invite-codes" className="space-y-1.5">
        {codes.map((code, i) => (
          <li key={code} className="flex items-center gap-2">
            <span className="w-5 shrink-0 text-right text-xs text-muted-foreground tabular-nums">
              {i + 1}
            </span>
            <code
              data-testid="fresh-invite-code"
              className="flex-1 rounded-md bg-background px-3 py-2 font-mono text-sm tracking-wider select-all"
            >
              {code}
            </code>
            <CopyButton value={registrationURL(code)} label="Copy registration link" />
            <CopyButton value={code} label="Copy code" variant="ghost" />
          </li>
        ))}
      </ul>

      <div className="mt-3">
        <Button variant="ghost" size="sm" onClick={onDone}>
          I have saved them
        </Button>
      </div>
    </div>
  );
}
